import { PlanTier } from './types'

/**
 * The ten detection signals (DEV-2422 §1.3).
 *
 * The id IS the `ProtectionInfoDTO` field, so the adapters never translate between
 * two vocabularies. `feature` is the `accountStore.hasFeature` key the API guards on
 * (`assets-api.ts:688-700`); a signal without one is included in every plan.
 */
export type SignalId =
	| 'geo'
	| 'bounced'
	| 'proxy'
	| 'bots'
	| 'ip_range'
	| 'multi_target'
	| 'interaction'
	| 'placements'
	| 'non_converting'
	| 'converted'

export interface SignalSpec {
	id: SignalId
	label: string
	description: string
	/** `hasFeature` key. Undefined = every plan carries it. */
	feature?: string
	tier?: PlanTier
	/** Reads conversions, so it does nothing until the conversion code is installed. */
	needsConversionTracking?: boolean
}

/**
 * Order is the mock's reading order: the first five fill the left column, the last
 * five the right. Deliberately fixed rather than sorted by tier, so the grid does not
 * reshuffle when a customer upgrades and the chips disappear.
 */
export const SIGNALS: SignalSpec[] = [
	{
		id: 'geo',
		label: 'Geolocation',
		description: 'Stop clicks from outside your advertising region.'
	},
	{
		id: 'bounced',
		label: 'Bounced clicks',
		description:
			'Flag visitors that waste your budget with repeated bounced clicks.'
	},
	{
		id: 'proxy',
		label: 'Proxies',
		description: 'Block traffic hiding behind proxies and VPNs.'
	},
	{
		id: 'bots',
		label: 'Bots',
		description: 'Keep automated bots from draining your budget.',
		feature: 'protection_bots',
		tier: 'Standard'
	},
	{
		id: 'ip_range',
		label: 'IP ranges',
		description: 'Stop coordinated attacks from neighboring IP addresses.',
		feature: 'protection_iprange',
		tier: 'Standard'
	},
	{
		id: 'multi_target',
		label: 'Multi-campaign',
		description: 'Prevent one visitor from draining several campaigns.',
		feature: 'protection_multitarget',
		tier: 'Standard'
	},
	{
		id: 'interaction',
		label: 'Website interactions',
		description: 'Block wasteful clicks from low engagement visitors.',
		feature: 'protection_interaction',
		tier: 'Standard'
	},
	{
		id: 'placements',
		label: 'Placements',
		description: 'Prevent fraudulent placements from showing your display ads.',
		feature: 'protection_placements',
		tier: 'Pro'
	},
	{
		id: 'non_converting',
		label: 'Non-converting',
		description: 'Block excessive non-converting traffic',
		feature: 'protection_conversion',
		tier: 'Pro',
		needsConversionTracking: true
	},
	{
		id: 'converted',
		label: 'Conversions',
		description: 'Stop showing ads to visitors that already converted',
		feature: 'protection_conversion',
		tier: 'Pro',
		needsConversionTracking: true
	}
]

/** Where the two-column grid splits. */
export const SIGNAL_COLUMN_SPLIT = 5
